import React, { useRef, useState, useEffect } from "react";
import { capitalizeName } from "../lib/utils/funcs";
import {
  patientNameMasking,
  petHospitalQueueImageClinics,
} from "../lib/utils/constants";
import SlotTimings from "./SlotTimings";
import { FaBell } from "react-icons/fa";
import { useTVConfiguration } from "../lib/contexts/TVConfigurationContext";
import Marquee from "react-fast-marquee";
import RoomTag from "./RoomTag";
import { MdMeetingRoom } from "react-icons/md";
import { useFeatureFlags } from "../lib/contexts/FeatureFlagContext";
import { maskPatientName } from "../lib/utils/maskPatientName";

const ColumnPatient = ({
  pos,
  name,
  prn,
  notStarted,
  onGoing,
  empty,
  text,
  status,
  queue_type,
  type,
  hospitalID,
  slotStartTime,
  slotEndTime,
  emergency,
  showRoomName,
  allotedRoom,
  onHold,
  transferStatus,
  recent_transfer_id,
  isTransferred,
}: {
  pos?: any;
  name?: string;
  prn?: string;
  notStarted?: boolean;
  onGoing?: boolean;
  empty?: boolean;
  text?: string;
  status?: number;
  queue_type?: string;
  type?: string;
  hospitalID?: string | undefined;
  slotStartTime?: string | undefined;
  slotEndTime?: string | undefined;
  emergency?: boolean;
  showRoomName?: boolean;
  allotedRoom?: string | undefined;
  onHold?: boolean;
  transferStatus?: number | undefined;
  recent_transfer_id?: string | null | undefined;
  isTransferred?: boolean;
}) => {
  const { tvConfig } = useTVConfiguration();
  const { mask_patient_name_tv, display_prn_tv } = useFeatureFlags();
  const lakeside_id = "7c8eb809-8062-42fe-b235-f5e759125bd5"
  const nameContainerRef = useRef<HTMLDivElement>(null);
  const nameTextRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const maskName = (fullName: string) => {
    if (!fullName) return "";

    const maskWord = (word: string) => {
      const len = word.length;
      if (len <= 3) {
        return word; 
      }
      if (len === 4) {
        return word.slice(0, 3) + "*";
      }
      return word.slice(0, -2) + "**";
    };
    const parts = fullName.trim().split(" ").filter(Boolean);
    const maskedParts = parts.map(maskWord);
    
    return maskedParts.join(" ");
  };
  useEffect(() => {
    const checkOverflow = () => {
      if (nameContainerRef.current && nameTextRef.current) {
        setIsOverflowing(
          nameTextRef.current.scrollWidth > nameContainerRef.current.clientWidth
        );
      }
    };

    checkOverflow();
    const timeout = setTimeout(checkOverflow, 200);
    window.addEventListener("resize", checkOverflow);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("resize", checkOverflow);
    };
  }, [name]);


  if (notStarted) {
    return (
      <div className="bg-darkBlue p-5 mt-5 rounded-xl w-full">
        <p className="text-white text-xl text-center">Queue has not started</p>
      </div>
    );
  }

  const renderName = () => {
    // display_prn_tv wins when the booking has a PRN — a PRN is not a name, so
    // it is shown as-is. With no PRN (the common case) the existing rules apply.
    const prnValue = display_prn_tv ? prn?.trim() : "";
    const maskedName = prnValue
      ? prnValue
      : mask_patient_name_tv
      ? maskPatientName(name)
      : hospitalID && patientNameMasking.includes(hospitalID)
      ? maskName(capitalizeName(name || ""))
      : capitalizeName(name || "");
    // Overflow heuristics follow whatever is actually rendered, so a long PRN
    // scrolls the same way a long name does.
    const shouldScroll =
      isOverflowing ||
      (maskedName && maskedName.length > 9) ||
      (maskedName && maskedName.trim().split(" ").length > 1);

    return (
      <div
        ref={nameContainerRef}
        className="flex-[2.8] overflow-hidden w-full relative"
      >
        <span
          ref={nameTextRef}
          className="absolute opacity-0 whitespace-nowrap"
        >
          {maskedName}
        </span>

        {shouldScroll ? (
          <div className="w-full overflow-hidden">
            <Marquee play pauseOnHover gradient={false} speed={50}>
              <span className="text-2xl font-medium uppercase whitespace-nowrap">
                {maskedName}&nbsp;&nbsp;&nbsp;
              </span>
            </Marquee>
          </div>
        ) : (
          <span className="text-2xl font-medium uppercase whitespace-nowrap block">
            {maskedName}
          </span>
        )}
      </div>
    );
  };

  if (onGoing) {
    return (
      <div
        className={`relative ${
          pos
            ? "flex flex-row bg-[#EBEBEB] mt-3 h-16 p-2 justify-center border-2 border-[#10B018] items-center rounded-full w-full"
            : "flex flex-row bg-[#EBEBEB] mt-3 h-[8vh] p-2 justify-center border-2 border-[#10B018] items-center rounded-full w-full"
        }`}
      >
        
        {!pos && !isTransferred && (!tvConfig || tvConfig.slot !== false) && (
          <SlotTimings
            start={slotStartTime}
            end={slotEndTime}
            className="!bg-black text-white"
          />
        )}

        {hospitalID === lakeside_id ? (
        // LAKESIDE ID ("old" style)
        pos && !isTransferred && (!tvConfig || tvConfig.token !== false) && (
          <p className="flex text-xl bg-black text-white h-12 w-12 mr-2 rounded-full justify-center items-center">
            {pos}
          </p>
        )
      ) : (
        // ALL OTHERS ("new" style)
        pos && !isTransferred && (!tvConfig || tvConfig.token !== false) && tvConfig?.patient_name !== false && (
          <p className="flex text-sm font-semibold bg-black text-white h-[30px] px-4 mr-2 rounded-full justify-center items-center">
            Token {pos}
          </p>
        )
      )}

        {isTransferred && (
          <span className="ml-1 px-3 py-1 text-sm font-bold bg-[#0F3CBE] text-white rounded-full">T</span>
        )}

        {tvConfig?.patient_name !== false ? (
        renderName() // Use the existing renderName function for consistency
      ) : // ELSE (patient_name is HIDDEN): Render TOKEN text if 'pos' exists
      pos && !isTransferred && (!tvConfig || tvConfig.token !== false) ? (
        // Use the same styling as renderName's outer div + span
        <div className="flex-[2.8] overflow-hidden w-full relative text-center">
           <span className="text-2xl font-medium uppercase whitespace-nowrap block">
             Token {pos}
           </span>
        </div>
      ) : (
        // Fallback: Render empty div if BOTH name and token are hidden
        <div className={`flex-[2.8] ml-4 ${!pos && "ml-0"}`}></div>
      )}

        {showRoomName && allotedRoom ? (
          <RoomTag
            text={allotedRoom}
            showText={true}
            size="small"
            icon={MdMeetingRoom}
          />
        ) : (
          (onHold ||
            status === 2 ||
            transferStatus === 2 ||
            (transferStatus !== undefined && [6, 7, 8, 9].includes(transferStatus)) ||
            !tvConfig ||
            tvConfig.booking_mode !== false) && (
            <p className={`text-[12px] font-semibold text-white rounded-full py-1 px-1 text-center mx-2 flex-[1] h-[30px] min-w-[80px] max-w-[80px] flex justify-center items-center transition-all duration-300 cursor-pointer ${onHold ? "bg-red-600" : "bg-[#10B018]"}`}>
              {onHold
                ? "On-Hold"
                : status === 2 || transferStatus === 2
                ? "Ongoing"
                : type === "walk-in"
                ? "Walk-in"
                : type === "followup"
                ? "Follow-up"
                : "Booking"}
            </p>
          )
        )}
        {emergency && (
          <FaBell className="text-red-500 text-lg mr-2 w-6 h-6 hover:animate-bounce cursor-pointer transition-all duration-300 hover:scale-110" />
        )}
      </div>
    );
  }

  if (empty) {
    const usePetQueueImage = petHospitalQueueImageClinics.includes(
      hospitalID || ""
    );

    return (
      <div className="flex flex-col justify-center items-center bg-white rounded-lg mt-10 p-5 h-[75%]">
        <img
          src={
            usePetQueueImage
              ? "https://ik.imagekit.io/gn9by35ed/vetClinicQueueImage1-removebg-preview.png?updatedAt=1748851163134"
              : require("../assets/images/empty.png")
          }
          alt="Queue empty"
          className="!w-[80%]"
        />
        <p className="text-center mb-5 font-medium">{text}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-row bg-[#EBEBEB] mt-3 h-16 p-2 justify-between items-center rounded-full gap-2">

      {!pos && !isTransferred && (!tvConfig || tvConfig.slot !== false) && (
        <SlotTimings start={slotStartTime} end={slotEndTime} className="mr-2" />
      )}

      {/* Render Token TAG only if 'pos' exists AND patient_name is VISIBLE */}
      {hospitalID === lakeside_id ? (
        // LAKESIDE ID ("old" style)
        pos && !isTransferred && (!tvConfig || tvConfig.token !== false) && (
          <p className="flex text-xl bg-black text-white h-12 w-12 mr-2 rounded-full justify-center items-center">
            {pos}
          </p>
        )
      ) : (
        // ALL OTHERS ("new" style)
        pos && !isTransferred && (!tvConfig || tvConfig.token !== false) && tvConfig?.patient_name !== false && (
          <p className="flex text-sm font-semibold bg-black text-white h-[30px] px-4 mr-2 rounded-full justify-center items-center">
            Token {pos}
          </p>
        )
      )}

      {isTransferred && (
        <span className="ml-1 px-3 py-1 text-sm font-bold bg-[#0F3CBE] text-white rounded-full">T</span>
      )}

      {/* Render NAME text if patient_name is VISIBLE */}
      {tvConfig?.patient_name !== false ? (
        renderName() // Use the existing renderName function
      ) : // ELSE (patient_name is HIDDEN): Render TOKEN text if 'pos' exists
      pos && !isTransferred && (!tvConfig || tvConfig.token !== false) ? (
        // Use the same styling as renderName's outer div + span
        <div className="flex-[2.8] overflow-hidden w-full relative text-center">
           <span className="text-2xl font-medium uppercase whitespace-nowrap block">
             Token {pos}
           </span>
        </div>
      ) : (
        // Fallback: Render empty div if BOTH name and token are hidden
        <div className={`flex-[2.8] ml-4 ${!pos && "ml-0"}`}></div>
      )}

      {onHold ? (
        <p className="text-[12px] font-semibold bg-red-600 text-white py-1 px-1 rounded-full text-center mx-2 flex-[1] h-[30px] min-w-[80px] max-w-[80px] flex justify-center items-center transition-all duration-300 cursor-pointer">
          On-Hold
        </p>
      ) : (
        (status === 2 ||
          transferStatus === 2 ||
          (transferStatus !== undefined && [6, 7, 8, 9].includes(transferStatus)) ||
          !tvConfig ||
          tvConfig.booking_mode !== false) && (
          <p
            className={`text-[12px] font-semibold py-1 px-1 rounded-full text-center mx-2 flex-[1] h-[30px] min-w-[80px] max-w-[80px] flex justify-center items-center transition-all duration-300 cursor-pointer
        ${
          // 🟪 In History (queue) — violet
          transferStatus === 6
            ? "bg-[#7C3AED] text-white border border-[#7C3AED]"
            : // 🟩 In History (ongoing) — green
            transferStatus === 7
            ? "bg-[#10B018] text-white border border-[#10B018]"
            : // 🟧 In Scan (queue) — orange
            transferStatus === 8
            ? "bg-[#EA580C] text-white border border-[#EA580C]"
            : // 🟩 In Scan (ongoing) — green
            transferStatus === 9
            ? "bg-[#10B018] text-white border border-[#10B018]"
            : // 🟩 Ongoing (normal)
            status === 2
            ? "bg-[#0F3CBE] text-white border border-[#10B018]"
            : // 🟦 Walk-in / Follow-up / Booking
            type === "walk-in" || type === "followup" || type === "booking"
            ? "bg-[#0F3CBE] text-white border border-[#00B7A8]"
            : // Default
              "bg-[#0F3CBE] text-white border border-[#00B7A8]"
        }
      `}
          >
            {transferStatus === 6 || transferStatus === 7
              ? "In History"
              : transferStatus === 8 || transferStatus === 9
              ? "In Scan"
              : status === 2 || transferStatus === 2
              ? "Ongoing"
              : type === "walk-in"
              ? "Walk-in"
              : type === "followup"
              ? "Follow-up"
              : "Booking"}
          </p>
        )
      )}
    </div>
  );
};

export default ColumnPatient;
